import { useEffect } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';
import { getCookie, setCookie } from 'cookies-next';

import { Maybe, AnalyticsContext } from '@/types/generated';
import { dataLayerConfig, sendFacebookEvent } from '@/utils';
import { useAppContext, useFacilityContext } from '@/context';
import { errorPageView } from '@/constants';
import { checkGADataExpiry } from '@/utils/checkGADataExpiry';
import { storeGADataToLocalStorage } from '@/utils/storeGADataToLocalStorage';
import { useInitialReferrer } from '@/index';

export interface IPageView {
	error?: string;
	analyticsContext?: Maybe<AnalyticsContext>;
	/**
	 * Specifies which app this gets called from.
	 * Eg: Marketing, Office Details etc
	 *
	 * @type {string}
	 * @memberof IPageView
	 */
	siteSection?: string;
	pageReferrer?: string;
}

const PageView = ({ error = '', analyticsContext, siteSection = '', pageReferrer }: IPageView) => {
	const { config, stateCodeFromHeaders } = useAppContext();
	const { facilityCode } = useFacilityContext();

	const searchParams = useSearchParams();
	const utmParams = searchParams.toString();
	const ccTransId = searchParams.get('trans_id');
	const ccCampaign = searchParams.get('cc_campaign');
	const ccMedium = searchParams.get('cc_medium');
	const ccSource = searchParams.get('cc_source');
	const ccContent = searchParams.get('cc_content');
	const ccTerm = searchParams.get('cc_term');
	const pathname = usePathname();

	useInitialReferrer();

	const enrichPayloadWithMarketingData = (existingParams: URLSearchParams, newParams: Record<string, string>) => {
		// Add new parameters to the existing set only if they don't already exist
		Object.keys(newParams).forEach((key) => {
			if (newParams[key] && !existingParams.has(key)) {
				// Set the new value only if it is not already present
				existingParams.set(key, newParams[key]);
			}
		});
		return existingParams;
	};

	useEffect(() => {
		dataLayerConfig({
			event: analyticsContext?.event || 'page_view',
			brand: config.name || analyticsContext?.brand || '',
			location: '',
			location_status: '',
			page_referrer: globalThis?.document?.referrer || '',
			...(analyticsContext ? analyticsContext : {}),
			...(error ? errorPageView[error] : {}),
			...(stateCodeFromHeaders && { state: stateCodeFromHeaders }),
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	useEffect(() => {
		if (!analyticsContext && facilityCode) {
			const isMobile = /mobile/i.test(navigator?.userAgent);
			const deviceType = isMobile ? 'mobile' : 'desktop';
			dataLayerConfig({
				event: 'virtual_page_view',
				brand: config.name,
				...(siteSection && { site_section: siteSection }),
				virtual_page_url: globalThis?.location?.href,
				virtual_page_title: globalThis?.document?.title,
				device_type: deviceType,
				website_selected_facility_id: facilityCode,
				...(pageReferrer && { page_origin_url: pageReferrer }),
				...(stateCodeFromHeaders && { state: stateCodeFromHeaders }),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [analyticsContext, facilityCode, pathname]);

	useEffect(() => {
		if (config.features.analytics.facebookPixel) {
			sendFacebookEvent('PageView');
		}

		if (config.name === 'ClearChoice') {
			if (utmParams) {
				if (ccTransId) {
					localStorage.setItem('ccTransId', ccTransId);
				}
				setCookie('marketing_source', searchParams.toString(), {
					path: '/',
					maxAge: 30 * 24 * 60 * 60 * 1000, // 30 days in milliseconds
				});
			}

			const existingMarketingSource = getCookie('marketing_source') as string | undefined;
			const existingParams = new URLSearchParams(existingMarketingSource || '');
			let newParams = {};

			// If the feature flag is on, create new params for utm_ matching cc_ params
			if (config.featureFlags.enableCcMarketingSourceTransformation) {
				newParams = {
					utm_campaign: ccCampaign || '',
					utm_medium: ccMedium || '',
					utm_source: ccSource || '',
					utm_term: ccTerm || '',
					utm_content: ccContent || '',
				};
			}

			const enrichedParams = enrichPayloadWithMarketingData(existingParams, newParams);

			const fullPayload = enrichedParams.toString();
			setCookie('marketing_source', fullPayload, {
				path: '/',
				maxAge: 30 * 24 * 60 * 60 * 1000,
			});

			checkGADataExpiry();
			storeGADataToLocalStorage();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};

export default PageView;
